/* ----------------------------------------------------------------------------
 * handsFreeConfig — the SINGLE kill switch for the whole "Hey Aura" hands-free
 * feature (owner directive 2026-07-19). Ships OFF by default. When false:
 *   - VoiceCopilot renders NO hands-free toggle and NO wake-armed visual;
 *   - useHandsFree is fully inert (no getUserMedia, no AudioContext, no worklet,
 *     no ONNX load, no BroadcastChannel, no listeners) — the entire wake code
 *     path is dead, so nothing can arm, listen, or reach the network.
 * The orchestrator flips this to `true` ONLY after the owner's real-device
 * acceptance. Nothing else in the app depends on it being on.
 * -------------------------------------------------------------------------- */

/** MASTER kill switch. OFF by default. Flip to true to enable Hey Aura. */
export const HANDS_FREE_ENABLED = false;

/** Wake-word decision threshold on the classifier score (0..1). At/above this
 *  a "Hey Aura" is accepted. Tuned on the delivered clean engine (owner: 0.40).
 *  Overridable at runtime via ?hfThreshold= for device tuning (see resolveThreshold). */
export const WAKE_THRESHOLD = 0.40;

/** localStorage key persisting the PER-DEVICE opt-in (a user must click once). */
export const HANDS_FREE_OPTIN_KEY = 'aura.handsfree.optin.v1';

/** BroadcastChannel / localStorage lock name for the single-tab guarantee. */
export const HANDS_FREE_LOCK_NAME = 'aura-handsfree-lock';

/** The wake-triggered greeting audio (played by useVoiceController on a wake
 *  turn). Kept here (main-safe) because the controller lives in the main bundle;
 *  it is a plain mp3 URL, not wake engine code. The wake ENGINE asset paths
 *  (models, ORT bundle, worklet) live in ./wakeAssets.ts so they stay OUT of the
 *  main bundle entirely (import-safety). */
export const WAKE_GREETING_URL = '/wake/how-can-i-help.mp3';

/** FOLLOW-UP wake prompt (owner FIX 2, 2026-07-25). The FIRST "Hey Aura" of a
 *  session plays the pre-recorded WAKE_GREETING_URL ("How can I help?"); every
 *  SUBSEQUENT wake in the SAME session instead SPEAKS this line via the existing
 *  TTS path — a natural "anything else?" continuation that needs NO new audio
 *  asset. Short + punctuated so the shared TTS renders it cleanly. */
export const FOLLOW_UP_WAKE_PROMPT = 'Is there anything else?';

/** POST-CLOSE COOLDOWN (D4 fix, voice-wave-4, owner re-test-2 2026-07-27): a
 *  turn-close (isTurnClose ack spoken) resets hasWoken and stamps a close
 *  timestamp. A wake fire inside this window is very likely the SAME marginal
 *  re-fire class the owner reproduced (a near-threshold score moments after an
 *  explicit close, echo/AGC/NS all off) rather than a genuine fresh "Hey Aura" —
 *  it is IGNORED (no greeting, no follow-up prompt, diag-logged) instead of
 *  audibly re-opening the loop the user just closed. A GENUINE wake AFTER the
 *  window plays a FRESH greeting (hasWoken was reset), never the "anything
 *  else?" follow-up. Bounded 8-10s per the fix proposal; not env-overridable
 *  (a fixed, evidence-picked UX constant, not an infra tunable). */
export const POST_CLOSE_COOLDOWN_MS = 9000;

/** POST-ANSWER SCORE GRACE (Phase C, voice-turn-audio-redesign, 2026-07-27 --
 *  the self-trigger fix): after Aura's own TTS ACTUALLY finishes playing (the
 *  turn entity's real onEnded-bound endAnswering() call, never a coarse
 *  VoiceStatus flip), the wake scorer stays gated for this many EXTRA ms
 *  before it is allowed to resume. Absorbs speaker/output-buffer tail +
 *  any residual echo the raw-capture fix (echo-cancellation OFF, 2026-07-24)
 *  lets reach the mic right at the tail of playback. Evidence: retest3
 *  wake_score_fire 0.8042 fired ONE SECOND into the SEO answer while the
 *  phase incorrectly read as safe to re-arm (rootcause:
 *  voice-turn-audio-redesign-scope-2026-07-27.md §2). Deliberately SHORT
 *  (not the 6s no-speech ceiling) -- a genuine "Hey Aura" said moments after
 *  Aura finishes must still land promptly; the real bleed-capture fixture
 *  (owner capture-exercise, still pending) is the outstanding proof point
 *  for further tuning -- documented honestly, not assumed final. */
export const POST_ANSWER_SCORE_GRACE_MS = 500;

/** ANSWERING re-arm retry cadence (Phase C): when a STATUS idle/error arrives
 *  while the turn entity still reports isAnsweringOrGrace()===true (the exact
 *  race this fix closes -- a coarse status flip racing ahead of the TRUE
 *  playback-end signal), the wake reducer HOLDS in 'processing' and the glue
 *  reschedules the SAME status re-check after this delay instead of re-arming
 *  immediately. Self-healing: if audio is still genuinely playing at the
 *  retry, the check holds again and reschedules once more, converging
 *  exactly when isAnsweringOrGrace() truly goes false -- so a real answer's
 *  end is never perceptibly delayed by more than one retry tick. */
export const ANSWERING_REARM_RETRY_MS = 250;

/** ANSWERING watchdog backstop (IVG FINDING-1 fix, 2026-07-27): if the turn
 *  entity reports `answering === true` but NO playback is actually live
 *  (player not playing AND no active SegmentedNarrator run) continuously for
 *  this long, the controller force-clears the answering window and beacons
 *  `wake_answering_backstop_cleared` (never a silent clear). This is defense
 *  in depth behind the cancelNarration choke point -- it guarantees
 *  `answering` can NEVER be stuck true with nothing playing (the
 *  hands-free-permanently-dead regression the IVG caught), no matter what
 *  future path forgets to close the window. 2000ms: far longer than any
 *  legitimate momentary not-live gap (the narrator's inter-segment 700ms
 *  pause reports the narrator itself as live, so it never counts here), yet
 *  fast enough that recovery is imperceptible next to the 6s no-speech
 *  capture ceiling. Evaluated inside isAnswering(), which the wake reducer's
 *  250ms hold/retry loop polls -- exactly the loop that would otherwise spin
 *  forever. */
export const WAKE_ANSWERING_BACKSTOP_MS = 2000;

/** The wake-prompt choice for a turn (pure, unit-tested). GREETING = play the
 *  cached mp3 asset (first wake); TTS = speak FOLLOW_UP_WAKE_PROMPT via the
 *  existing synth path (every wake after). The controller supplies hasWoken
 *  (has a wake greeting already played this session). */
export type WakePrompt = { kind: 'greeting' } | { kind: 'tts'; text: string };
export function selectWakePrompt(hasWoken: boolean): WakePrompt {
  return hasWoken ? { kind: 'tts', text: FOLLOW_UP_WAKE_PROMPT } : { kind: 'greeting' };
}

/** Resolve the active threshold: the compiled default, or a ?hfThreshold= query
 *  override clamped to (0,1] for on-device tuning without a rebuild. Pure. */
export function resolveThreshold(search?: string, fallback: number = WAKE_THRESHOLD): number {
  if (!search) return fallback;
  try {
    const q = new URLSearchParams(search).get('hfThreshold');
    if (q === null) return fallback;
    const n = Number(q);
    if (Number.isFinite(n) && n > 0 && n <= 1) return n;
  } catch {
    /* malformed query — keep the default */
  }
  return fallback;
}

/* ---- per-DEVICE runtime enable (GAP-1) ------------------------------------
 * A default-OFF, per-device override so the owner can turn Hey Aura on for THEIR
 * device (e.g. visit https://app.bookedcentral.com/?handsfree=1 once) while the
 * GLOBAL kill switch HANDS_FREE_ENABLED stays false for everyone else. The
 * effective enable is HANDS_FREE_ENABLED || runtimeHandsFreeEnabled().
 * -------------------------------------------------------------------------- */

/** localStorage key persisting the per-device runtime enable. */
export const HANDS_FREE_RUNTIME_KEY = 'bc_hf_enable';

/** Minimal storage seam (localStorage in the browser; injectable in tests). */
export interface KVStore {
  getItem(k: string): string | null;
  setItem(k: string, v: string): void;
  removeItem(k: string): void;
}

/** Per-device runtime enable. `?handsfree=1` PERSISTS the opt-in on this device
 *  and returns true; `?handsfree=0` CLEARS it and returns false; otherwise the
 *  persisted value; default OFF (no param + no key => false). Pure given its
 *  injected search + store (defaults to window.location.search + localStorage). */
/** Override TTL (owner override-lifecycle directive 2026-07-26): the ?handsfree=1
 *  device override AUTO-EXPIRES so it can never stick indefinitely on a device.
 *  It is a QA/dev convenience, not a consent surface — 24h is plenty. */
export const HANDS_FREE_OVERRIDE_TTL_MS = 24 * 60 * 60 * 1000;

/** Explicitly clear the device override (used when the user selects Standard —
 *  an explicit "never listen" choice retires any QA override on this device). */
export function clearRuntimeOverride(store?: KVStore): void {
  const kv: KVStore | undefined =
    store ?? (typeof window !== 'undefined' && window.localStorage ? window.localStorage : undefined);
  try { kv?.removeItem(HANDS_FREE_RUNTIME_KEY); } catch { /* private mode */ }
}

export function runtimeHandsFreeEnabled(search?: string, store?: KVStore, nowMs?: number): boolean {
  const kv: KVStore | undefined =
    store ?? (typeof window !== 'undefined' && window.localStorage ? window.localStorage : undefined);
  const qs = search ?? (typeof window !== 'undefined' ? window.location?.search : undefined);
  const now = nowMs ?? Date.now();
  let param: string | null = null;
  if (qs) {
    try { param = new URLSearchParams(qs).get('handsfree'); } catch { param = null; }
  }
  if (param === '1') {
    // TTL-stamped (owner 2026-07-26): expires in 24h instead of sticking forever.
    try { kv?.setItem(HANDS_FREE_RUNTIME_KEY, JSON.stringify({ v: 1, ts: now })); } catch { /* private mode */ }
    return true;
  }
  if (param === '0') {
    try { kv?.removeItem(HANDS_FREE_RUNTIME_KEY); } catch { /* private mode */ }
    return false;
  }
  try {
    const raw = kv?.getItem(HANDS_FREE_RUNTIME_KEY);
    if (!raw) return false;
    // LEGACY bare '1' (pre-TTL): deliberately treated as EXPIRED and cleared —
    // this un-sticks every device stuck by the pre-2026-07-26 format without
    // waiting for a ?handsfree=0 visit (the consent-bug remediation).
    if (raw === '1') { kv?.removeItem(HANDS_FREE_RUNTIME_KEY); return false; }
    const parsed = JSON.parse(raw) as { v?: number; ts?: number };
    if (typeof parsed?.ts !== 'number') { kv?.removeItem(HANDS_FREE_RUNTIME_KEY); return false; }
    if (now - parsed.ts > HANDS_FREE_OVERRIDE_TTL_MS) { kv?.removeItem(HANDS_FREE_RUNTIME_KEY); return false; }
    return true;
  } catch { return false; }
}

/* ---- WakeVisualState (GAP: import-safety) ---------------------------------
 * The PLAIN, wake-free shape the lazy HandsFreeController reports UP to
 * VoiceCopilot so the main bundle can paint the "Hey Aura" strip WITHOUT any
 * static import of wake code. Lives here (a pure config module) precisely so
 * VoiceCopilot can `import type { WakeVisualState }` without pulling the engine.
 * -------------------------------------------------------------------------- */
export interface WakeVisualState {
  /** the user has opted in on this device (per-device persistence). */
  optedIn: boolean;
  /** arming / model load in flight. */
  loading: boolean;
  /** mic open and actively listening for "Hey Aura". */
  armed: boolean;
  /** wake (and TTS) suspended by the single Mute control. */
  muted: boolean;
  /** hands-free is active in another tab (single-tab guarantee). */
  otherTab: boolean;
  /** a human-readable wake error, or null. */
  error: string | null;
}

/* ---- ACCOUNT-LEVEL mic mode (owner directive 2026-07-24) ------------------
 * "Hey Aura" hands-free is now an ACCOUNT-LEVEL preference (bc_users, follows
 * the human across every workspace), NOT the per-device ?handsfree=1 override.
 *   standard   = press-to-talk only; the wake engine NEVER arms (safe default).
 *   continuous = always-listening "Hey Aura".
 *   both       = continuous + press-to-talk (functionally identical wake gate).
 * The ?handsfree=1 param + HANDS_FREE_ENABLED remain a dev/QA + master override.
 * -------------------------------------------------------------------------- */

/** The account mic-mode enum (server-validated; safe-defaults to 'standard'). */
export type AuraMicMode = 'standard' | 'continuous' | 'both';

/** The three valid mic modes, in UI order. */
export const AURA_MIC_MODES: readonly AuraMicMode[] = ['standard', 'continuous', 'both'] as const;

/** Coerce any wire value to a valid mic mode, defaulting SAFELY to 'standard'
 *  (so an absent column / unknown value can never arm the wake engine). Pure. */
export function coerceMicMode(v: unknown): AuraMicMode {
  return v === 'continuous' || v === 'both' ? v : 'standard';
}

/** True when the account mic mode makes the wake engine ELIGIBLE. Only
 *  'continuous'/'both' arm; 'standard' never does. Pure. */
export function micModeWantsWake(mode: AuraMicMode): boolean {
  return mode !== 'standard';
}

/** Inputs to the app-wide wake-enablement gate (all resolved at the shell). */
export interface WakeEnablementInputs {
  /** the account mic-mode preference (default 'standard'). */
  micMode: AuraMicMode;
  /** the compiled master kill switch (HANDS_FREE_ENABLED). */
  masterEnabled: boolean;
  /** the per-device dev/QA override (?handsfree=1 persisted). */
  runtimeOverride: boolean;
  /** the /v1/ai/voice/status probe — never arm where voice is unavailable. */
  voiceAvailable: boolean;
  /** this browser can actually run the wake pipeline (getUserMedia+AudioContext). */
  browserSupported: boolean;
  /** the mic mode is the SERVER value (explicit user choice), not the safe
   *  default-fallback. Drives the explicit-Standard-wins-absolutely rule. */
  modeExplicit?: boolean;
}

/** THE app-wide wake gate (pure, unit-tested). The account mic mode is the real
 *  driver; the master switch + dev override are additional escape hatches. Wake
 *  is eligible ONLY where voice is available AND the browser can run it. Note:
 *  'standard' with no override => NEVER eligible (the product guarantee). */
export function resolveWakeEnablement(i: WakeEnablementInputs): boolean {
  // CONSENT INVARIANT (owner directive 2026-07-26): an EXPLICIT Standard
  // selection ("Aura never listens until you hold the button") wins ABSOLUTELY —
  // over the ?handsfree override AND the compiled master switch. Checked first,
  // unconditionally: no OR-chain ordering can reintroduce the hazard.
  if (i.modeExplicit === true && i.micMode === 'standard') return false;
  if (!i.voiceAvailable || !i.browserSupported) return false;
  return micModeWantsWake(i.micMode) || i.masterEnabled || i.runtimeOverride;
}

/* ---- ?handsfree=1 override GATE (consent guard, owner directive 2026-07-25) --
 * SECURITY: the per-device ?handsfree=1 dev/QA override forces the wake engine
 * to arm even when the ACCOUNT mic mode is standard — i.e. it can start the
 * always-listening mic WITHOUT the account-level consent the mic-mode preference
 * records. That is acceptable for an authenticated OWNER (self-consent) or in a
 * NON-PRODUCTION build (dev/staging), but MUST NOT let an arbitrary visitor arm
 * a non-owner mic in production merely by appending a URL param. This pure
 * predicate decides whether the requested override is PERMITTED to take effect;
 * when false, ?handsfree=1 is inert and the normal consent-gated (mic-mode)
 * path stands. -------------------------------------------------------------- */
export interface RuntimeOverrideContext {
  /** the ?handsfree=1 per-device override is set (runtimeHandsFreeEnabled()). */
  overrideRequested: boolean;
  /** the build is NON-production (dev/staging), where QA overrides are allowed. */
  isNonProd: boolean;
}

/** OWNER DECISION 2026-07-26 (override retired from production): ?handsfree=1
 *  may arm ONLY in a NON-production build. In production it is structurally
 *  INERT for everyone — including workspace owners; hands-free in production is
 *  enabled exclusively via the account Settings toggle (with consent capture).
 *  This ELIMINATES the override-consent bug class (it previously armed an
 *  owner's prod session and could outrank an explicit Standard until TTL).
 *  Second override-caused consent bug was the trigger; the Settings toggle has
 *  replaced the override's original job. Pure. */
export function effectiveRuntimeOverride(c: RuntimeOverrideContext): boolean {
  return c.overrideRequested && c.isNonProd;
}

/** The single-tab lock name, NAMESPACED by user id so a logout/login (or two
 *  different users sharing one browser profile) re-contest the mic explicitly
 *  rather than inheriting a stale cross-user lock. Falls back to the bare lock
 *  name when no user id is available. Pure. */
export function handsFreeLockName(userId?: string | number | null): string {
  const ns = userId === undefined || userId === null || userId === '' ? '' : String(userId);
  return ns === '' ? HANDS_FREE_LOCK_NAME : `${HANDS_FREE_LOCK_NAME}:${ns}`;
}


/* ---- DEVICE mic kill (owner #2.2 #8, 2026-07-25) ---------------------------
 * A one-tap "Turn off mic" in the Aura popup DISARMS THIS DEVICE ONLY. It does
 * NOT flip the account mic-mode preference (owner decision: muting one device
 * must not change the account-wide setting). Persisted per-device; the provider
 * gates the engine mount on it, and Settings > Aura Voice offers the revive. */
export const HANDS_FREE_DEVICE_OFF_KEY = 'bc_hf_device_off';

/** TRUE when the user killed the mic on THIS device (per-device persistence). */
export function deviceMicOff(store?: KVStore): boolean {
  const kv: KVStore | undefined =
    store ?? (typeof window !== 'undefined' && window.localStorage ? window.localStorage : undefined);
  try { return kv?.getItem(HANDS_FREE_DEVICE_OFF_KEY) === '1'; } catch { return false; }
}

/** Persist / clear the per-device mic kill. Account mode is NEVER touched. */
export function setDeviceMicOff(on: boolean, store?: KVStore): void {
  const kv: KVStore | undefined =
    store ?? (typeof window !== 'undefined' && window.localStorage ? window.localStorage : undefined);
  try {
    if (on) kv?.setItem(HANDS_FREE_DEVICE_OFF_KEY, '1');
    else kv?.removeItem(HANDS_FREE_DEVICE_OFF_KEY);
  } catch { /* private mode — session-only kill still applies via provider state */ }
}
